import React from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router";
import { useNavigate } from "react-router-dom";
import { get, isEmpty, startCase } from "lodash";
import { TalentPickData } from "@tvg/ts-types/TalentPicks";
import { UnaryFn } from "@tvg/ts-types/Functional";
import buildRaceUrl, { buildTalentPicksTVG5Url } from "@tvg/formatter/url";
import { WagerTypeCodesInvertedEnum } from "@tvg/ts-types/Wager";
import { AccountRolesEnum } from "@tvg/ts-types/Account";
import formatCurrency from "@tvg/formatter/currency";
import { RaceStatusEnum } from "@tvg/ts-types/Race";
import { getAccountNumber } from "@urp/store-selectors";
import {
  EVENTS,
  LINK_TEXT
} from "@urp/amplitude/src/modules/talentPicks/constants";
import {
  Avatar,
  BodyText,
  Box,
  Button,
  Flex,
  Switch
} from "@fanduel/formation-react-components";
import BetSummary from "./BetSummary";
import {
  CardWrapper,
  CardHeader,
  ActionsWrapper,
  AmountContainer
} from "./styled-components";
import MtpBody from "./MtpBody";
import { isFdr } from "../../helpers";
import { triggerAmplitudeEvent, getModule } from "../../utils/amplitude";
import { useUpdateTalentPick } from "../../hooks";
import { BASE_QA_LABEL } from "../../constants";
import { userRoles } from "../../redux/selectors";

interface Props {
  talentPick: TalentPickData;
  cardPosition: number;
  showRaceSummaryInfo?: boolean;
  showViewRaceButton?: boolean;
  showAddToBetslipButton?: boolean;
  qaLabel?: string;
  onAddToBetslip?: UnaryFn<TalentPickData, void>;
  numWagerableRunners?: string[];
  onHomePage?: boolean;
  isFDTVPage?: boolean;
  canCreatePicks?: boolean;
  width?: string;
}

const TalentPicksCard = ({
  talentPick,
  cardPosition,
  showAddToBetslipButton = true,
  showViewRaceButton = true,
  showRaceSummaryInfo = true,
  qaLabel = "talentPick-card",
  onAddToBetslip,
  numWagerableRunners,
  onHomePage = false,
  isFDTVPage = false,
  canCreatePicks = false,
  width
}: Props) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { pathname, search } = location;
  const params = new URLSearchParams(search);
  const source = Number(params.get("race"));

  const accountNumber = useSelector(getAccountNumber);
  const accountRoles = useSelector(userRoles);

  const {
    talent,
    track: trackCode,
    race: raceNumber,
    trackName,
    amount: wagerAmount,
    price: betTotal,
    wagerType,
    visualSelections,
    numRunnersAllRaces,
    published
  } = talentPick;
  const { name: talentName, pictureUrl: talentPicture } = talent;
  const {
    abbreviation: wagerTypeCode,
    isKey,
    isBox,
    legCount,
    isWheel
  } = wagerType;
  const isFDR = isFdr();
  const raceURL = buildRaceUrl(trackCode, trackName, raceNumber);
  const switchRef = React.useRef<HTMLDivElement | null>(null);
  const { updateTalentPick, isLoadingPickEdit, isPublishedSwitchOn } =
    useUpdateTalentPick(published);
  const isCardDisabled =
    canCreatePicks && (!isPublishedSwitchOn || isLoadingPickEdit);

  const module = getModule(pathname);
  const amplitudePayload = {
    module,
    talentName,
    trackName,
    raceNumber,
    wagerTypeCode,
    cardPosition,
    selectionSource: module
  };

  const handleViewRace = () => {
    triggerAmplitudeEvent(EVENTS.CARD_CLICKED, {
      ...amplitudePayload,
      raceURL,
      linkText: LINK_TEXT.VIEW_RACE
    });
    navigate(buildRaceUrl(trackCode, trackName, raceNumber));
  };

  const handleAddToBetslip = () => {
    triggerAmplitudeEvent(EVENTS.ADD_TO_BETSLIP, {
      ...amplitudePayload,
      module: "Race Card",
      betTotal
    });
    triggerAmplitudeEvent(EVENTS.CARD_CLICKED, {
      ...amplitudePayload,
      raceURL,
      linkText: LINK_TEXT.ADD_TO_BETSLIP
    });

    if (onAddToBetslip) {
      onAddToBetslip(talentPick);

      if (source !== raceNumber) {
        navigate(buildTalentPicksTVG5Url(talentPick));
      }
    } else {
      navigate(buildTalentPicksTVG5Url(talentPick));
    }
  };

  const handleOnCardHeaderClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!(switchRef.current && switchRef.current.contains(e.target as Node))) {
      navigate(raceURL);
      triggerAmplitudeEvent(EVENTS.CARD_CLICKED, {
        ...amplitudePayload,
        raceURL,
        linkText: LINK_TEXT.TALENT_NAME
      });
    }
  };

  const handleOnSwitchChange = () => {
    const updatedTalentPick = {
      ...talentPick,
      published: !isPublishedSwitchOn,
      // BE expects favorites to be an array of arrays, never an empty array
      favorites: isEmpty(talentPick.favorites) ? [[]] : talentPick.favorites
    };

    updateTalentPick(updatedTalentPick);
  };

  const rawWagerType = get(WagerTypeCodesInvertedEnum, wagerTypeCode);

  const wagerTypeName = rawWagerType
    ? startCase(rawWagerType.replace("_", " ").toLowerCase())
    : "";

  const isAccountTalent =
    !accountRoles.includes(AccountRolesEnum.WAGEROPS) &&
    accountRoles.includes(AccountRolesEnum.TALENT);

  const shouldShowPublishSwitch =
    canCreatePicks &&
    (accountRoles.includes(AccountRolesEnum.WAGEROPS) ||
      (isAccountTalent && talent.tvgAccountId === accountNumber)) &&
    talentPick.raceStatus !== RaceStatusEnum.RACE_OFFICIAL &&
    talentPick.raceStatus !== RaceStatusEnum.RACE_OFF;

  return (
    <CardWrapper
      className="tpWrapper"
      data-qa-label={qaLabel}
      isOnHomePage={onHomePage}
      isFDR={isFDR}
      width={width}
    >
      <CardHeader
        isOnHomePage={onHomePage}
        onClick={handleOnCardHeaderClick}
        isFDTVPage={isFDTVPage}
      >
        <Box
          borderRadius="50%"
          overflow="hidden"
          opacity={isCardDisabled ? 0.5 : 1}
          marginRight="space2"
        >
          <Avatar
            alt={`${talentName} Avatar`}
            src={talentPicture}
            size={40}
            data-qa-label={`${BASE_QA_LABEL}-talent-avatar`}
          />
        </Box>
        {showRaceSummaryInfo && (
          <Flex
            data-qa-label={`${BASE_QA_LABEL}-race-summary-info`}
            flex={1}
            justifyContent="space-between"
            marginLeft="space3"
          >
            <Flex direction="column" justifyContent="center">
              <BodyText
                fontWeight="var(--fd-font-weights-font-weight-030)"
                data-qa-label={`${BASE_QA_LABEL}-talent-name`}
                color={
                  isCardDisabled
                    ? "var(--fd-colors-content-disabled)"
                    : "var(--fd-colors-content-default)"
                }
              >
                {talentName}
              </BodyText>
              <Flex justifyContent="flex-start" alignItems="flex-start">
                <AmountContainer>
                  <BodyText
                    data-qa-label={`${BASE_QA_LABEL}-bet-amount-n-type`}
                    color={
                      isCardDisabled
                        ? "var(--fd-colors-content-disabled)"
                        : "var(--fd-colors-content-subtle)"
                    }
                    fontWeight="var(--fd-font-weights-font-weight-020)"
                    marginRight="space1"
                  >{`${formatCurrency(
                    +wagerAmount
                  )} ${wagerTypeCode}`}</BodyText>
                </AmountContainer>
                <AmountContainer>
                  <BodyText
                    data-qa-label={`${BASE_QA_LABEL}-bet-total-n-type`}
                    color={
                      isCardDisabled
                        ? "var(--fd-colors-content-disabled)"
                        : "var(--fd-colors-content-subtle)"
                    }
                    fontWeight="var(--fd-font-weights-font-weight-020)"
                    marginLeft="space1"
                  >
                    {`${formatCurrency(+betTotal)} Ticket`}
                  </BodyText>
                </AmountContainer>
              </Flex>
            </Flex>
          </Flex>
        )}
        {shouldShowPublishSwitch && (
          <Switch
            ref={switchRef}
            data-qa-label={`${BASE_QA_LABEL}-pick-publish-switch`}
            label="Hidden Label"
            on={isPublishedSwitchOn}
            disabled={isLoadingPickEdit}
            labelPosition="hidden"
            onChange={handleOnSwitchChange}
          />
        )}
      </CardHeader>
      <Box
        maxHeight={showAddToBetslipButton ? "150px" : "220px"}
        overflow="auto"
        marginRight="space4"
      >
        <MtpBody qaLabel="tp-body-mtp" talentPick={talentPick} />
        <BetSummary
          wagerAmount={wagerAmount}
          betTotal={betTotal}
          selections={visualSelections || []}
          wagerTypeCode={wagerTypeCode}
          wagerTypeName={wagerTypeName}
          isKey={isKey}
          isBox={isBox}
          isWheel={isWheel}
          legCount={legCount}
          numWagerableRunners={numRunnersAllRaces || numWagerableRunners}
          raceNumber={raceNumber?.toString()}
          onHandicaping
        />
      </Box>
      <ActionsWrapper isOnHomePage={onHomePage}>
        {showViewRaceButton && (
          <Flex width={onHomePage ? "35%" : "auto"}>
            <Button
              data-qa-label={`${BASE_QA_LABEL}-view-race-button`}
              variant="tertiary"
              label="View Race"
              onClick={handleViewRace}
              disabled={isCardDisabled}
              fill
            />
          </Flex>
        )}
        {showAddToBetslipButton && (
          <Flex width={onHomePage ? "62%" : "auto"}>
            <Button
              data-qa-label={`${BASE_QA_LABEL}-add-to-betslip-button`}
              variant="primary"
              label="Add to Betslip"
              onClick={handleAddToBetslip}
              disabled={isCardDisabled}
              fill
            />
          </Flex>
        )}
      </ActionsWrapper>
    </CardWrapper>
  );
};

export default TalentPicksCard;
